import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  CircularProgress
} from '@mui/material';
import axios from 'axios';
import { SectorSelect, type Sector } from '../common/SectorSelect';

interface CreateAccountModalProps {
  open: boolean;
  onClose: () => void;
  onAccountCreated: () => void;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const CreateAccountModal: React.FC<CreateAccountModalProps> = ({ open, onClose, onAccountCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedSector, setSelectedSector] = useState<Sector | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orgInfo, setOrgInfo] = useState<{ accountLimit?: number; plan?: string } | null>(null);
  const [accountCount, setAccountCount] = useState<number>(0);
  
  useEffect(() => {
    const fetchLimits = async () => {
      if (!open) return;
      try {
        const orgResp = await axios.get(`${API_URL}/api/organization`, { withCredentials: true });
        setOrgInfo(orgResp.data.organization || null);
        const acctResp = await axios.get(`${API_URL}/api/accounts`, { withCredentials: true, params: { limit: 1 }});
        setAccountCount(acctResp.data.pagination?.total || 0);
      } catch {
        // non-blocking
      }
    };
    fetchLimits();
  }, [open]);
  
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    // Validate inputs
    if (!name.trim()) {
      setError('Account name is required');
      return;
    }
    
    if (!selectedSector) {
      setError('Please select a business sector');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);

      await axios.post(`${API_URL}/api/accounts`, {
        name: name.trim(),
        description: description.trim() || undefined,
        sectorId: selectedSector.id
      }, {
        withCredentials: true
      });
      
      // Reset form and close modal
      resetForm();
      onAccountCreated();
      onClose();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to create account';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  const resetForm = () => {
    setName('');
    setDescription('');
    setSelectedSector(null);
    setError(null);
  };
  
  const handleClose = () => {
    resetForm();
    onClose();
  };
  
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Account</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {orgInfo && orgInfo.plan !== 'INTERNAL' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Accounts: {accountCount} / {orgInfo.accountLimit ?? 0}
            </Alert>
          )}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Enter the details for the new account
          </Typography>
          
          <TextField
            label="Account Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            margin="normal"
            required
            autoFocus
            disabled={loading}
          />
          
          <TextField
            label="Brand Description (Recommended)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={3}
            disabled={loading}
            helperText="Provide a brief description of your brand to help us generate more accurate content"
            inputProps={{ maxLength: 500 }}
          />
          
          <SectorSelect
            value={selectedSector}
            onChange={setSelectedSector}
            required
            disabled={loading}
            error={!!error && !selectedSector}
            helperText={!selectedSector && error?.includes('sector') ? error : undefined}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Box sx={{ position: 'relative' }}>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              disabled={loading}
            >
              Create Account
            </Button>
            {loading && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateAccountModal; 
